import { I18n } from "@assets/strings";
import React from "react";
import { Link } from "react-router";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { Author } from "@domain/lms/content";
import { Report } from "@domain/report/report";
import { capitalizeFirst } from "@utils/strings";
import { TertiaryButton } from "@ui/components/buttons/tertiaryButton";
import { Primary, PrimaryMedium } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { Secondary } from "@ui/components/containers/textBlocks";
import { Row, Spacer, stack } from "@ui/components/layout";
import { UserView } from "@ui/components/profile/userView";
import { gap } from "@ui/styles/utils";

interface ReportViewProps extends React.HTMLAttributes<HTMLDivElement> {
	report: Report;
}
export const ReportView: React.FC<ReportViewProps> = ({ report, ...htmlProps }) => {
	const { teasing, author, path } = getReportInfos(report);

	// const { run: moderate, loading: moderateLoading } = useAsync(async () => {
	// 	try {
	// 		await adminContentService.moderate(report.id);
	// 		createToast(I18n.t("success"), I18n.t("report.moderate.success"), "success");
	// 	} catch (e) {
	// 		createToast(I18n.t("error"), I18n.t("report.moderate.error"), "error");
	// 	}
	// }, [report.id]);

	// const askModerationConfirmation = useConfirmation(
	// 	I18n.t("report.moderate.title"),
	// 	I18n.t("report.view.moderate"),
	// 	I18n.t("report.moderate.message")
	// );

	return (
		<Container {...htmlProps} handled={report.status === "CONTENT_DELETED"}>
			<ReportContent>
				<Row gap={5} align="center" wrap>
					<Primary>
						{I18n.t("report.view.header", { contentType: capitalizeFirst(I18n.t(`content.${report.contentType}`)) })}
					</Primary>
					<UserView user={report.reporter} />
					<Spacer />
					<Secondary>{report.date.toLocaleDateString()}</Secondary>
				</Row>
				{teasing && <PrimaryMedium>{teasing}</PrimaryMedium>}
				{path && (
					<Link to={path}>
						<TertiaryButton>{I18n.t("report.view.link")}</TertiaryButton>
					</Link>
				)}
			</ReportContent>
			{author && (
				<ReportAuthor>
					<Primary>{I18n.t("report.view.author")}</Primary>
					<UserView user={author} showDescription />
				</ReportAuthor>
			)}
			<Primary>
				<b>{I18n.t("report.view.motive")}</b>
				<span>{I18n.t(`report.motive.${report.motive}`)}</span>
			</Primary>
			{/* {report.status === "PENDING" ? (
				report.contentType === "USER" ? null : (
					<Row gap={20}>
						<LoadingButton onClick={() => askModerationConfirmation(() => moderate())} loading={moderateLoading}>
							{I18n.t("report.view.moderate")}
						</LoadingButton>
					</Row>
				)
			) : (
				<PrimaryBold>{I18n.t("report.view.moderated")}</PrimaryBold>
			)} */}
		</Container>
	);
};

const Container = styled(RoundedBlock)<{ handled?: boolean }>`
	padding: 20px;
	${gap(20)};
	${({ handled }) => handled && "opacity: 0.4"};
`;

const ReportContent = styled.div`
	${stack(10, "flex-start", "stretch")};
`;

const ReportAuthor = styled.div`
	${stack(10)};
	max-width: 360px;
`;

function getReportInfos(report: Report): { teasing?: string; author?: Author; path?: string } {
	if (!report.content) {
		return {};
	}
	switch (report.contentType) {
		case "COURSE":
			return {
				path: Path.Lms.Course(report.content.id),
				author: report.content.author,
				teasing: report.content.title,
			};
		case "ARTICLE":
			return {
				path: Path.Article(report.content.id),
				author: report.content.author,
				teasing: report.content.title,
			};
		case "CAROUSEL":
			return {
				path: Path.Carousel(report.content.id),
				author: report.content.author,
				teasing: report.content.title,
			};
		case "QUIZ":
			return {
				path: Path.Quiz(report.content.id),
				author: report.content.author,
				teasing: report.content.title,
			};
		case "POST":
			return {
				path: Path.Post(report.content.id),
				author: report.content.author,
				teasing: report.content.text.getPlainText(),
			};
		case "POST_COMMENT":
			return {
				author: report.content.author,
				teasing: report.content.text.getPlainText(),
			};
		case "ORGANIZATION":
			return {
				path: Path.Organization(report.content),
				teasing: report.content.primaryName,
			};
		case "GROUP":
			return {
				path:
					report.content.type === "DISCUSSION_GROUP" ? Path.Discussion(report.content) : Path.Shareroom(report.content),
				author: report.content.owner,
				teasing: report.content.name,
			};
		case "USER":
			return {
				path: Path.Profile(report.content),
				author: report.content,
			};
	}
}
